'use client';

import { useEffect, useRef, useState } from 'react';
import { UiVaultConfig } from 'src/@types/vaults';
import { VaultDepositWithdrawFormWithGradientWrapper } from './VaultDepositWithdrawForm';
import { UserPerformance } from './UserPerformance';
import { UIMarket } from '@drift/common';
import { SpotMarketConfig } from '@drift-labs/sdk';
import VaultOverview from './VaultOverview';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import ScrollShadows from 'src/components/ScrollShadows';
import { useVaultOnChainData } from 'src/hooks/query/vaults/base/useVaultOnChainData';
import { useVaultDepositorAccounts } from 'src/hooks/query/vaults/base/useVaultDepositorAccounts';
import { VaultPerformanceTabContent } from '../VaultPage/VaultPerformanceTabContent';
import { VAULT_VIEW_TAB_OPTIONS } from '../../../constants/constants';
import VaultOverviewMarkdownContent from './VaultOverview/v2Components/VaultOverviewMarkdownContent';
import VaultOverviewContactsBox from './VaultOverview/v2Components/VaultOverviewContactsBox';
import { SACRED_WITHDRAWAL_VAULT_DESCRIPTION } from '../../../constants/vaults/misc';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import { VaultTagsAlt } from '../VaultTagsAlt';
import { useVaultDepositorHistories } from 'src/hooks/query/vaults/base/useVaultDepositorHistories';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';

type ContentTab = keyof typeof VAULT_VIEW_TAB_OPTIONS;

const CONTENT_TAB_OPTIONS: { value: ContentTab; label: string }[] = [
	{
		value: 'VaultPerformance',
		label: 'Vault Performance',
	},
	{
		value: 'UserPerformance',
		label: 'Your Performance',
	},
	{
		value: 'Overview',
		label: 'Overview',
	},
];

const VaultMainContentSidePanel = (props: { children: React.ReactNode }) => {
	return (
		<div className="flex w-[308px] min-w-[308px] flex-col gap-4 lg:w-[360px] lg:min-w-[360px]">
			{props.children}
		</div>
	);
};

export const VaultMainContent = (props: {
	uiVaultConfig: UiVaultConfig;
	initialTab?: keyof typeof VAULT_VIEW_TAB_OPTIONS;
}) => {
	const vaultPubkey = props.uiVaultConfig.vaultPubkeyString;
	const { data: onChainData } = useVaultOnChainData();
	const { data: depositorAccounts, isLoading: isDepositorLoading } =
		useVaultDepositorAccounts();
	const vaultAccountData = onChainData?.[vaultPubkey]?.vaultAccountData;
	const vaultDepositorAccountData =
		depositorAccounts?.[vaultPubkey]?.vaultDepositor;
	const isVaultDepositorLoaded = !isDepositorLoading;
	const { captureEvent } = usePostHogCapture();

	const { data: historiesLookup } = useVaultDepositorHistories();
	const vaultDepositorHistory =
		historiesLookup?.[props.uiVaultConfig.vaultPubkeyString];

	const shouldShowUserPerformanceTab =
		!!vaultDepositorAccountData &&
		!!vaultAccountData &&
		!!vaultDepositorHistory;

	const contentTabOptions = CONTENT_TAB_OPTIONS.filter((opt) => {
		if (opt.value === 'UserPerformance') {
			return shouldShowUserPerformanceTab;
		}
		return true;
	});

	const [activeTab, setActiveTab] = useState<ContentTab>(
		props.initialTab ?? contentTabOptions[0].value
	);

	const hasAutoSwitchedRef = useRef(false);

	useEffect(() => {
		if (shouldShowUserPerformanceTab && !hasAutoSwitchedRef.current) {
			setActiveTab('UserPerformance');
			hasAutoSwitchedRef.current = true;
		}
	}, [shouldShowUserPerformanceTab]);

	const isMobile = useIsMobileScreenSize();

	const scrollableTabsRef = useRef<HTMLDivElement>(null);

	return (
		<div className="flex w-full flex-col gap-y-md sm:gap-y-2xl">
			<div className="flex w-full flex-col gap-6">
				<div className="flex w-full flex-col-reverse items-start justify-between gap-y-lg md:flex-row md:items-center">
					<ScrollShadows
						scrollableRef={scrollableTabsRef}
						gradientFromClassName="from-main-bg"
						className="w-full"
					>
						<div
							ref={scrollableTabsRef}
							className="hidden-scroll overflow-x-auto"
						>
							<Tabs
								size="lg"
								options={contentTabOptions}
								activeTab={activeTab}
								onTabChange={(tab) => {
									setActiveTab(tab);

									if (tab === 'Overview') {
										captureEvent('view_overview_strat_vault');
									}
								}}
							/>
						</div>
					</ScrollShadows>
					<VaultTagsAlt uiVaultConfig={props.uiVaultConfig} />
				</div>
			</div>
			{props.uiVaultConfig.isSacredWithdrawalVault && (
				<InlineAlert
					variant="info"
					title={SACRED_WITHDRAWAL_VAULT_DESCRIPTION}
				/>
			)}
			<div className="flex gap-x-lg">
				<div className="flex w-full flex-col gap-6">
					{activeTab === 'VaultPerformance' && (
						<VaultPerformanceTabContent uiVaultConfig={props.uiVaultConfig} />
					)}
					{activeTab === 'UserPerformance' && (
						<UserPerformance
							depositAssetConfig={
								UIMarket.createSpotMarket(props.uiVaultConfig.depositAsset)
									.market as SpotMarketConfig
							}
							vaultPubkey={props.uiVaultConfig.vaultPubkeyString}
						/>
					)}
					{activeTab === 'Overview' && (
						<VaultOverview uiVaultConfig={props.uiVaultConfig} />
					)}
				</div>

				{!isMobile && (
					<VaultMainContentSidePanel>
						<VaultDepositWithdrawFormWithGradientWrapper
							type="deposit"
							uiVaultConfig={props.uiVaultConfig}
							vaultDepositorAccountData={vaultDepositorAccountData}
							isVaultDepositorLoaded={isVaultDepositorLoaded}
							vaultAccountData={vaultAccountData}
						/>

						{props.uiVaultConfig.useMarkdownContent && (
							<>
								<VaultOverviewMarkdownContent
									markdownContent={
										props.uiVaultConfig.vaultSidePanelMarkdownContent ?? ''
									}
								/>
								<VaultOverviewContactsBox
									contacts={
										props.uiVaultConfig.vaultSidePanelContactInformation
									}
								/>
							</>
						)}
					</VaultMainContentSidePanel>
				)}
			</div>
		</div>
	);
};
